// Prefer relative reverse proxy to eliminate client secret leakage
const PROXY_AUTH_BASE = '/api/auth';
const PROXY_DATA_BASE = '/api/data';

export const ADMIN_EMAIL = 'y.yang.priv@gmail.com';
export const ADMIN_USER_ID = 'kgTvpLt2CdGO3kdiAmxw33a4GVwQXKYE';

export async function authFetch(path, options = {}) {
  try {
    const res = await fetch(`${PROXY_AUTH_BASE}/${path}`, {
      method: options.method || 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      body: options.body,
    });
    // Accept valid responses and client auth errors (400, 401, 403); fallback on 404 or server 5xx
    if (res.status !== 404 && res.status < 500) return res;
  } catch (_) {
    // Fallback to direct NCB if proxy network fails in standalone mode
  }

  const directUrl = import.meta.env.VITE_NCB_AUTH_URL || 'https://app.nocodebackend.com/api/user-auth';
  return fetch(`${directUrl}/${path}`, {
    method: options.method || 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${import.meta.env.VITE_NCB_SECRET_KEY || ''}`,
      'X-Database-Instance': import.meta.env.VITE_NCB_INSTANCE || '53038_memark',
      ...options.headers,
    },
    body: options.body,
  });
}

export async function dataFetch(path, options = {}) {
  try {
    const res = await fetch(`${PROXY_DATA_BASE}/${path}`, {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      body: options.body,
    });
    if (res.status !== 404 && res.status < 500) return res;
  } catch (_) {
    // Fallback to direct NCB
  }

  const directUrl = import.meta.env.VITE_NCB_DATA_URL || 'https://app.nocodebackend.com/api/data';
  const separator = path.includes('?') ? '&' : '?';
  const instance = import.meta.env.VITE_NCB_INSTANCE || '53038_memark';
  const url = `${directUrl}/${path}${separator}Instance=${instance}`;
  return fetch(url, {
    method: options.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${import.meta.env.VITE_NCB_SECRET_KEY || ''}`,
      'X-Database-Instance': instance,
      ...options.headers,
    },
    body: options.body,
  });
}

async function readWhere(table, params) {
  const qs = Object.entries(params)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  const res = await dataFetch(`read/${table}?${qs}&limit=500`);
  const d = await res.json();
  if (Array.isArray(d.data)) return d.data;
  if (d.data && typeof d.data === 'object') return [d.data];
  return [];
}

// ── Auth ───────────────────────────────────────────────────────────────────

export async function signIn(email, password) {
  const res = await authFetch('sign-in/email', { body: JSON.stringify({ email, password }) });
  const data = await res.json();
  if (data.token && data.user) {
    localStorage.setItem('ncb_token', data.token);
    localStorage.setItem('ncb_user', JSON.stringify(data.user));
    return data.user;
  }
  throw new Error(data.message || 'Login failed');
}

export async function signUp(email, password, name) {
  const res = await authFetch('sign-up/email', { body: JSON.stringify({ email, password, name }) });
  const data = await res.json();
  if (data.token && data.user) {
    localStorage.setItem('ncb_token', data.token);
    localStorage.setItem('ncb_user', JSON.stringify(data.user));
    return data.user;
  }
  throw new Error(data.message || 'Signup failed');
}

export async function forgotPassword(email) {
  const res = await authFetch('forgot-password', { body: JSON.stringify({ email }) });
  return res.json();
}

export function getSession() {
  const stored = localStorage.getItem('ncb_user');
  if (!stored) return null;
  try {
    const user = JSON.parse(stored);
    if (user && user.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
      user.id = ADMIN_USER_ID;
    }
    return user;
  } catch (_) {
    return null;
  }
}

export function signOut() {
  localStorage.removeItem('ncb_token');
  localStorage.removeItem('ncb_user');
}

// ── Roles ──────────────────────────────────────────────────────────────────

export async function getUserRole(userId, email) {
  if (email === ADMIN_EMAIL) {
    try {
      const rows = await readWhere('user_roles_notely', { email: ADMIN_EMAIL });
      if (!rows.length) {
        await dataFetch('create/user_roles_notely', {
          method: 'POST',
          body: JSON.stringify({ owner_id: userId, email: ADMIN_EMAIL, role: 'admin' }),
        });
      } else if (rows[0].owner_id !== userId) {
        await dataFetch(`update/user_roles_notely/${rows[0].id}`, {
          method: 'PUT',
          body: JSON.stringify({ owner_id: userId }),
        });
      }
    } catch (e) {
      console.warn('Admin row sync failed:', e);
    }
    return 'admin';
  }
  try {
    const rows = await readWhere('user_roles_notely', { owner_id: userId });
    const row = rows.find((r) => r.owner_id === userId && r.email !== ADMIN_EMAIL);
    return row ? row.role : null;
  } catch (e) {
    console.error('getUserRole error:', e);
    return null;
  }
}

export async function createPendingRole(userId, email) {
  try {
    const rows = await readWhere('user_roles_notely', { owner_id: userId });
    if (rows.find((r) => r.owner_id === userId)) return;
    const res = await dataFetch('create/user_roles_notely', {
      method: 'POST',
      body: JSON.stringify({ owner_id: userId, email, role: 'pending' }),
    });
    const result = await res.json();
    console.log('Created pending role:', result);
  } catch (e) {
    console.error('createPendingRole error:', e);
    throw e;
  }
}

export async function fetchAllUsers() {
  const res = await dataFetch('read/user_roles_notely?limit=500');
  const d = await res.json();
  const rows = Array.isArray(d.data) ? d.data : [];
  return rows.filter((u) => u.email !== ADMIN_EMAIL);
}

export async function approveUser(roleId) {
  const res = await dataFetch(`update/user_roles_notely/${roleId}`, {
    method: 'PUT',
    body: JSON.stringify({ role: 'approved' }),
  });
  return res.json();
}

export async function rejectUser(roleId) {
  const res = await dataFetch(`update/user_roles_notely/${roleId}`, {
    method: 'PUT',
    body: JSON.stringify({ role: 'rejected' }),
  });
  return res.json();
}

// ── Bookmarks ──────────────────────────────────────────────────────────────

export async function fetchBookmarks(userId) {
  const rows = await readWhere('bookmarks_notely', { owner_id: userId });
  return rows.filter((b) => b.owner_id === userId);
}

export async function createBookmark(payload) {
  const res = await dataFetch('create/bookmarks_notely', { method: 'POST', body: JSON.stringify(payload) });
  return res.json();
}

export async function updateBookmark(id, payload) {
  const res = await dataFetch(`update/bookmarks_notely/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
  return res.json();
}

export async function deleteBookmark(id) {
  const res = await dataFetch(`delete/bookmarks_notely/${id}`, { method: 'DELETE' });
  return res.json();
}

// ── Folders ────────────────────────────────────────────────────────────────

export async function fetchFolders(userId) {
  const rows = await readWhere('folders_notely', { owner_id: userId });
  return rows.filter((f) => f.owner_id === userId);
}

export async function createFolder(payload) {
  const res = await dataFetch('create/folders_notely', { method: 'POST', body: JSON.stringify(payload) });
  return res.json();
}

export async function updateFolder(id, payload) {
  const res = await dataFetch(`update/folders_notely/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
  return res.json();
}

export async function deleteFolder(id) {
  const res = await dataFetch(`delete/folders_notely/${id}`, { method: 'DELETE' });
  return res.json();
}

// ── Settings ───────────────────────────────────────────────────────────────

export async function fetchSettings(userId) {
  const rows = await readWhere('user_settings_notely', { owner_id: userId });
  return rows.find((r) => r.owner_id === userId) || null;
}

export async function saveSettings(id, payload) {
  if (id != null) {
    const res = await dataFetch(`update/user_settings_notely/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
    return res.json();
  } else {
    const res = await dataFetch('create/user_settings_notely', { method: 'POST', body: JSON.stringify(payload) });
    return res.json();
  }
}

// ── Shared Folders ─────────────────────────────────────────────────────────

export async function shareFolder({ folderId, folderTitle, ownerEmail, ownerId, inviteeEmail, permission = 'viewer' }) {
  const existing = await readWhere('shared_folders_notely', { folder_id: folderId });
  let sharedFolderId;
  if (existing.length > 0) {
    sharedFolderId = existing[0].id;
  } else {
    sharedFolderId = crypto.randomUUID();
    await dataFetch('create/shared_folders_notely', {
      method: 'POST',
      body: JSON.stringify({
        id: sharedFolderId,
        folder_id: folderId,
        owner_id: ownerId,
        owner_email: ownerEmail,
        folder_title: folderTitle,
      }),
    });
  }

  const members = await readWhere('shared_folder_members_notely', { shared_folder_id: sharedFolderId });
  const alreadyInvited = members.find((m) => m.invitee_email === inviteeEmail);
  if (alreadyInvited) {
    throw new Error(`${inviteeEmail} is already in this shared folder.`);
  }

  let inviteeOwnerId = null;
  try {
    const roleRows = await readWhere('user_roles_notely', { email: inviteeEmail });
    if (roleRows.length > 0) inviteeOwnerId = roleRows[0].owner_id;
  } catch (_) {}

  const memberId = crypto.randomUUID();
  await dataFetch('create/shared_folder_members_notely', {
    method: 'POST',
    body: JSON.stringify({
      id: memberId,
      shared_folder_id: sharedFolderId,
      folder_id: folderId,
      invitee_email: inviteeEmail,
      invitee_owner_id: inviteeOwnerId,
      status: 'accepted',
      permission: permission === 'editor' ? 'editor' : 'viewer',
    }),
  });

  return { sharedFolderId, memberId };
}

export async function updateSharedMemberPermission(memberId, permission) {
  const res = await dataFetch(`update/shared_folder_members_notely/${memberId}`, {
    method: 'PUT',
    body: JSON.stringify({ permission }),
  });
  return res.json();
}

export async function updateSharedMemberLocalParent(memberId, localParentId) {
  const res = await dataFetch(`update/shared_folder_members_notely/${memberId}`, {
    method: 'PUT',
    body: JSON.stringify({ local_parent_id: localParentId }),
  });
  return res.json();
}

export async function removeSharedMember(memberId) {
  // 1. Get the member record to find its shared_folder_id
  const memberRows = await readWhere('shared_folder_members_notely', { id: memberId });
  let sfId = null;
  if (memberRows.length > 0) {
    sfId = memberRows[0].shared_folder_id;
  }

  // 2. Delete the member record
  const res = await dataFetch(`delete/shared_folder_members_notely/${memberId}`, { method: 'DELETE' });
  const result = await res.json();

  // 3. If this was the last member, delete the shared_folders_notely record
  if (sfId) {
    try {
      const remaining = await readWhere('shared_folder_members_notely', { shared_folder_id: sfId });
      if (remaining.length === 0) {
        await dataFetch(`delete/shared_folders_notely/${sfId}`, { method: 'DELETE' });
      }
    } catch (e) {
      console.warn('Failed to cleanup shared_folders_notely:', e);
    }
  }

  return result;
}

export async function fetchSharedMembers(folderId) {
  const sfRows = await readWhere('shared_folders_notely', { folder_id: folderId });
  if (!sfRows.length) return [];
  const sharedFolderId = sfRows[0].id;
  const members = await readWhere('shared_folder_members_notely', { shared_folder_id: sharedFolderId });
  return members;
}

export async function fetchSharedWithMe(userEmail) {
  const memberRows = await readWhere('shared_folder_members_notely', { invitee_email: userEmail });
  if (!memberRows.length) return [];

  const results = [];
  for (const m of memberRows) {
    // 1. Find the shared_folders_notely record
    const sfRows = await readWhere('shared_folders_notely', { id: m.shared_folder_id });

    // If the shared_folders record is gone → owner deleted it, skip entirely
    if (!sfRows.length) {
      // Silently clean up the dangling member record so it won't appear again
      try { await removeSharedMember(m.id); } catch (_) {}
      continue;
    }

    const sharedFolder = sfRows[0];

    // 2. Verify the ACTUAL folder still exists in folders_notely
    let liveTitle = null;
    try {
      const actualFolderRows = await readWhere('folders_notely', { id: sharedFolder.folder_id });
      if (actualFolderRows.length > 0 && actualFolderRows[0].title) {
        // Folder exists — use its live title
        liveTitle = actualFolderRows[0].title;
      }
    } catch (_) {}

    // 3. If the actual folder no longer exists → owner deleted the folder
    //    Clean up the orphaned records and skip showing this folder
    if (!liveTitle) {
      try {
        await removeSharedMember(m.id);
      } catch (_) {}
      continue;
    }

    // 4. Folder is alive — push it with the live title
    results.push({
      ...sharedFolder,
      folder_title: liveTitle,
      memberRecord: m,
      permission: m.permission || 'viewer',
    });
  }

  return results;
}

// Fetch ALL bookmarks inside a folder regardless of owner_id
export async function fetchSharedFolderBookmarks(folderId) {
  const res = await dataFetch(`read/bookmarks_notely?folder_id=${encodeURIComponent(folderId)}&limit=500`);
  const d = await res.json();
  return Array.isArray(d.data) ? d.data : [];
}

// Fetch ALL subfolders with a given parent_id regardless of owner_id
export async function fetchSharedFolderSubfolders(parentId) {
  const res = await dataFetch(`read/folders_notely?parent_id=${encodeURIComponent(parentId)}&limit=500`);
  const d = await res.json();
  return Array.isArray(d.data) ? d.data : [];
}

// ── Deep delete helpers ────────────────────────────────────────────────────

/**
 * Recursively delete ALL bookmarks and subfolders inside a real DB folder ID.
 * Does NOT delete the folder itself — caller does that.
 */
export async function deepDeleteFolderContents(realFolderId) {
  const [bookmarks, subfolders] = await Promise.all([
    fetchSharedFolderBookmarks(realFolderId),
    fetchSharedFolderSubfolders(realFolderId),
  ]);

  await Promise.all(bookmarks.map((b) => deleteBookmark(b.id)));

  for (const sub of subfolders) {
    await deepDeleteFolderContents(sub.id);
    await deleteFolder(sub.id);
  }
}

/**
 * Fully delete a shared folder root:
 * - Recursively deletes all bookmarks + subfolders inside
 * - Deletes the root folder itself
 * - Removes all shared_folder_members_notely rows
 * - Removes the shared_folders_notely row
 */
export async function deleteSharedFolderRoot(realFolderId) {
  // 1. Delete all contents recursively
  await deepDeleteFolderContents(realFolderId);

  // 2. Delete the root folder itself
  await deleteFolder(realFolderId);

  // 3. Clean up ALL sharing records so invitees auto-clear on next load
  try {
    const sfRows = await readWhere('shared_folders_notely', { folder_id: realFolderId });
    if (sfRows.length > 0) {
      const sfId = sfRows[0].id;
      const members = await readWhere('shared_folder_members_notely', { shared_folder_id: sfId });
      await Promise.all(members.map((m) => removeSharedMember(m.id)));
      await dataFetch(`delete/shared_folders_notely/${sfId}`, { method: 'DELETE' });
    }
  } catch (e) {
    console.warn('Could not clean sharing records:', e);
  }
}